import React from 'react';
import { Button, Paper, Stack, Typography } from '@mui/material';
import ArrowForwardRoundedIcon from '@mui/icons-material/ArrowForwardRounded';
import EventAvailableRoundedIcon from '@mui/icons-material/EventAvailableRounded';
import PersonAddAltRoundedIcon from '@mui/icons-material/PersonAddAltRounded';
import ReceiptLongRoundedIcon from '@mui/icons-material/ReceiptLongRounded';

import { CS_PRIMARY } from './dashboardHomeTokens';

const gettingStartedButtonIconSx = {
  '& .MuiButton-startIcon > :nth-of-type(1), & .MuiButton-endIcon > :nth-of-type(1)': {
    width: 14,
    height: 14,
    fontSize: '14px !important',
  },
} as const;

const gettingStartedIconStyle = {
  width: 14,
  height: 14,
  fontSize: 14,
} as const;

const gettingStartedButtonSx = {
  minHeight: 38,
  px: 2,
  borderRadius: '10px',
  textTransform: 'none',
  whiteSpace: 'nowrap',
  fontSize: 14,
  fontWeight: 600,
  lineHeight: 1.25,
  ...gettingStartedButtonIconSx,
} as const;

export interface DashboardGettingStartedPanelProps {
  onAddClient: () => void;
  onSetAvailability: () => void;
  onSetupBilling: () => void;
}

export const DashboardGettingStartedPanel: React.FC<DashboardGettingStartedPanelProps> = ({
  onAddClient,
  onSetAvailability,
  onSetupBilling,
}) => (
  <Paper
    elevation={0}
    sx={{
      border: 'none',
      borderRadius: '14px',
      bgcolor: '#fff',
      px: { xs: 2, sm: 2.5 },
      py: 2,
    }}
  >
    <Stack direction={{ xs: 'column', md: 'row' }} spacing={2} alignItems={{ md: 'center' }} justifyContent="space-between">
      <Stack spacing={0.5} sx={{ minWidth: 0 }}>
        <Typography sx={{ fontSize: 16, fontWeight: 700, color: '#0f172a', lineHeight: 1.3 }}>
          Getting started
        </Typography>
        <Typography sx={{ fontSize: 13, color: '#4a5565', lineHeight: 1.45 }}>
          Add your first client, set availability, and confirm billing so this dashboard has work to track.
        </Typography>
      </Stack>

      <Stack
        direction="row"
        spacing={1}
        useFlexGap
        sx={{
          flexWrap: { xs: 'wrap', md: 'nowrap' },
          flexShrink: 0,
          justifyContent: { xs: 'flex-start', md: 'flex-end' },
          minWidth: 0,
        }}
      >
        <Button
          variant="contained"
          startIcon={<PersonAddAltRoundedIcon style={gettingStartedIconStyle} />}
          endIcon={<ArrowForwardRoundedIcon style={gettingStartedIconStyle} />}
          onClick={onAddClient}
          data-analytics-event="Create Client Clicked"
          data-analytics-id="dashboard_add_first_client"
          data-analytics-label="Add First Client"
          data-analytics-section="dashboard_getting_started"
          sx={{
            ...gettingStartedButtonSx,
            bgcolor: CS_PRIMARY,
            boxShadow: 'none',
            '&:hover': { bgcolor: CS_PRIMARY, opacity: 0.92, boxShadow: 'none' },
          }}
        >
          Add first client
        </Button>
        <Button
          variant="outlined"
          startIcon={<EventAvailableRoundedIcon style={gettingStartedIconStyle} />}
          onClick={onSetAvailability}
          data-analytics-id="dashboard_set_availability"
          data-analytics-label="Set Availability"
          data-analytics-section="dashboard_getting_started"
          sx={{
            ...gettingStartedButtonSx,
            borderColor: '#b9ddeb',
            color: '#175b73',
          }}
        >
          Set availability
        </Button>
        <Button
          variant="outlined"
          startIcon={<ReceiptLongRoundedIcon style={gettingStartedIconStyle} />}
          onClick={onSetupBilling}
          data-analytics-id="dashboard_billing_settings"
          data-analytics-label="Billing Settings"
          data-analytics-section="dashboard_getting_started"
          sx={{
            ...gettingStartedButtonSx,
            borderColor: '#b9ddeb',
            color: '#175b73',
          }}
        >
          Billing settings
        </Button>
      </Stack>
    </Stack>
  </Paper>
);
